// IndexedDB-backed write-through for in-flight workout telemetry.
//
// Purpose: if the tab dies mid-workout (battery, OS-kill, accidental
// refresh), we want to recover the data on the next app load. localStorage
// would block the main thread on every flush; IDB writes are async and
// don't compete with the 5 MB localStorage quota that's already busy.
//
// Schema: one database `khronos-telemetry`, one object store `inFlight`
// keyed by `sessionId` (a UUID generated when the workout starts).
// Value shape: { sessionId, startedAt, startedAtWall, samples }.
//
// Native IDB only — no `idb` dep. Three operations: put, get, delete,
// list. All return Promises.

const DB_NAME = "khronos-telemetry";
const DB_VERSION = 1;
const STORE = "inFlight";

const openDb = () =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not available"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "sessionId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const tx = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    let out;
    try {
      out = fn(store);
    } catch (err) {
      reject(err);
      return;
    }
    t.oncomplete = () => {
      db.close();
      resolve(out);
    };
    t.onerror = () => {
      db.close();
      reject(t.error);
    };
    t.onabort = () => {
      db.close();
      reject(t.error || new Error("IDB transaction aborted"));
    };
  });
};

const reqAsPromise = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

/**
 * Idempotent write. Subsequent calls with the same sessionId overwrite.
 * Errors are swallowed and logged — IDB failure must not crash the workout.
 */
export const idbPut = async (sessionId, payload) => {
  try {
    await tx("readwrite", (store) => store.put({ sessionId, ...payload }));
  } catch (err) {
    console.warn("[telemetry/idb] put failed", err);
  }
};

export const idbGet = async (sessionId) => {
  try {
    return await tx("readonly", (store) => reqAsPromise(store.get(sessionId)));
  } catch (err) {
    console.warn("[telemetry/idb] get failed", err);
    return null;
  }
};

export const idbDelete = async (sessionId) => {
  try {
    await tx("readwrite", (store) => store.delete(sessionId));
  } catch (err) {
    console.warn("[telemetry/idb] delete failed", err);
  }
};

export const idbList = async () => {
  try {
    return await tx("readonly", (store) => reqAsPromise(store.getAll()));
  } catch (err) {
    console.warn("[telemetry/idb] list failed", err);
    return [];
  }
};
